import AllInclusiveIcon from '@mui/icons-material/AllInclusive';
import {
  DialogContent,
  DialogTitle,
  Box,
  Typography,
  Grid,
  Chip,
} from '@mui/material';
import { IconButton } from '@mui/material';
import { Tooltip } from '@mui/material';
import { Dialog } from '@mui/material';
import { FC, PropsWithChildren, useMemo, useState } from 'react';
import ReactJson from 'react-json-view';
import {
  DateIntervalDisplay,
  GenericRelativeDateDisplay,
} from './GenericDateFunctionField';
import { useInlineMemo } from '@/util/useMemoStyles';

type IngestionRecord = {
  when: string;
  op: string;
  ingestedData: any;
};

export type IngestionTrackerButtonProps = {
  ingestionRecords?: IngestionRecord[] | null;
};

export const IngestionTrackerButton = ({
  ingestionRecords,
}: IngestionTrackerButtonProps) => {
  const [open, setOpen] = useState(false);

  const reversedIngestionRecords = useMemo(
    () => ingestionRecords?.slice().reverse(),
    [ingestionRecords],
  );

  return (
    <>
      <Tooltip title="View ingestion records">
        <IconButton onClick={() => setOpen(true)}>
          <AllInclusiveIcon />
        </IconButton>
      </Tooltip>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle
          sx={useInlineMemo({
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          })}
        >
          <Typography variant="h6">Ingestion Tracker</Typography>
        </DialogTitle>
        <DialogContent>
          {reversedIngestionRecords?.map((record, index) => (
            <Box
              key={`${record.op}-${record.when}-${index}`}
              flexDirection="column"
              marginTop="2rem"
            >
              <Box
                marginBottom="1rem"
                display="flex"
                flexDirection="row"
              >
                <IngestionRecordView label="Operation">
                  <Chip
                    label={record.op}
                    size="small"
                    color={
                      record.op === 'PUT' ? 'primary' : 'secondary'
                    }
                  />
                </IngestionRecordView>
                <IngestionRecordView label="Time">
                  <GenericRelativeDateDisplay date={record.when} />
                </IngestionRecordView>
                {index < reversedIngestionRecords.length - 1 && (
                  <IngestionRecordView label="From Last Operation">
                    <DateIntervalDisplay
                      date1={
                        reversedIngestionRecords[index + 1]?.when
                      }
                      date2={record.when}
                    />
                  </IngestionRecordView>
                )}
              </Box>
              <Box
                sx={{
                  backgroundColor: '#2d2d2d',
                  p: 2,
                  borderRadius: 1,
                }}
              >
                <ReactJson
                  src={record.ingestedData}
                  theme="monokai"
                  displayDataTypes={false}
                  enableClipboard={true}
                  collapsed={2}
                  style={{
                    backgroundColor: 'transparent',
                    fontFamily: 'monospace',
                  }}
                />
              </Box>
            </Box>
          ))}
        </DialogContent>
      </Dialog>
    </>
  );
};

type IngestionRecordViewProps = {
  label: string;
} & PropsWithChildren;

export const IngestionRecordView: FC<IngestionRecordViewProps> = ({
  label,
  children,
}) => (
  <Grid marginRight="2rem">
    <Grid item>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
    </Grid>
    <Grid item>{children}</Grid>
  </Grid>
);
